import { useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { setSnackbar } from 'flow/actions/app-actions';
import SimpleModal from 'flow/components/ui-components/SimpleModal';
import useAppContext from 'flow/hooks/useAppContext';
import { CallRecord, TranscriptionExtended } from 'flow/types/CallRecord';
import { TypedMap } from 'flow/types/TypedMap';
import { PageType } from 'flow/components/call-history-v2/types';
import TranscriptionEmpty from './TranscriptionEmpty';
import TranscriptionMetadata from './TranscriptionMetadata';
import TranscriptionSegments from './TranscriptionSegments';
import TranscriptionTextView, { getDisplayText } from './TranscriptionTextView';
import TranscriptionToolbar from './TranscriptionToolbar';
import { View } from './types';

interface Props {
    callRecord: TypedMap<CallRecord>;
    transcriptionExtended?: TranscriptionExtended;
    plainTranscript?: string;
    plainSummary?: string;
    pageType?: PageType;
}

const ModalTranscription = ({
    callRecord,
    transcriptionExtended,
    plainTranscript,
    plainSummary,
    pageType,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [view, setView] = useState<View>('transcript');

    const hasExtendedTranscript = !!transcriptionExtended?.rawTranscript;
    const hasPlainTranscript = !!plainTranscript;
    const hasPlainSummary = !!plainSummary;
    const hasAnyTranscript = hasExtendedTranscript || hasPlainTranscript;

    // The summary toggle is only meaningful when a summary actually exists:
    // extended transcriptions always carry one, legacy ones may, and voice
    // message transcripts never do.
    const canToggleSummary = hasExtendedTranscript || hasPlainSummary;

    const copyToClipboard = (text: string) => {
        navigator?.clipboard?.writeText(text).then(() => {
            dispatch(
                setSnackbar({
                    message: i18next.t('snackbar.copied_to_clipboard'),
                }),
            );
        });
    };

    const displayText = hasExtendedTranscript
        ? getDisplayText(transcriptionExtended, view)
        : view === 'summary'
          ? (plainSummary ?? '')
          : (plainTranscript ?? '');

    return (
        <SimpleModal
            open={true}
            header={i18next.t('ai.transcript.transcript')}
            className="w-[600px]"
            footer={
                hasAnyTranscript ? (
                    <TranscriptionToolbar
                        view={view}
                        onSetView={setView}
                        onCopy={() => copyToClipboard(displayText)}
                        showSummaryToggle={canToggleSummary}
                    />
                ) : undefined
            }
        >
            <div className="flex min-h-[400px] flex-col gap-3 pb-4 text-sm antialiased">
                {transcriptionExtended && (
                    <TranscriptionMetadata
                        callRecord={callRecord}
                        duration={transcriptionExtended.duration}
                        pageType={pageType}
                    />
                )}

                {!hasAnyTranscript && <TranscriptionEmpty />}

                {hasExtendedTranscript && view === 'transcript' && (
                    <TranscriptionSegments
                        segmentations={transcriptionExtended.segmentations}
                    />
                )}

                {hasExtendedTranscript && view !== 'transcript' && (
                    <TranscriptionTextView
                        transcription={transcriptionExtended}
                        view={view}
                    />
                )}

                {hasPlainTranscript && !hasExtendedTranscript && (
                    <div className="whitespace-pre-line">
                        {view === 'summary' ? plainSummary : plainTranscript}
                    </div>
                )}
            </div>
        </SimpleModal>
    );
};

export default ModalTranscription;
